import { apiClient } from './authApi';
import { createBranchBody, createCompanyBody, createSubCompanyBody, createSubCustomerBody, updateBranchBody, updateCompanyBody, updateSubCompanyBody, updateSubCustomerBody } from '@carboncare/schemas';
import { preflight } from './schemaPreflight';
import { Company, CreateCompanyData, SubCustomer, CreateSubCustomerData, SubCompany, CreateSubCompanyData, Branch, CreateBranchData } from '../../types/company';
export interface LicenceTypeStatItem {
  licenceType: string;
  count: number;
}

export interface LicenceStatsResponse {
  total: number;
  byLicenceType: LicenceTypeStatItem[];
}

export type CompanyStatusFilter = 'active' | 'inactive' | 'demo';

export interface CompanyListParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  query?: string;
  sortOrder?: 'asc' | 'desc';
  licenceType?: string;
  status?: CompanyStatusFilter;
  fields?: string;
}

export interface CompanyListResponse {
  data: Company[];
  total: number;
  page: number;
  limit: number;
}

export const companyApi = {
  // Company CRUD operations
  getAll: async (params?: CompanyListParams): Promise<CompanyListResponse> => {
    const response = await apiClient.get('/companies', { params });
    // Support both paginated ({ data, total, page, limit }) and plain array response shapes
    if (Array.isArray(response.data.data)) {
      return {
        data: response.data.data,
        total: response.data.pagination?.total ?? response.data.total ?? response.data.data.length,
        page: response.data.pagination?.page ?? response.data.page ?? params?.page ?? 1,
        limit: response.data.pagination?.limit ?? response.data.limit ?? params?.limit ?? 10,
      };
    }
    return response.data;
  },

  uploadLogo: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await apiClient.post('/companies/upload-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data.logoUrl;
  },

  exportExcel: async (licenceType?: string, status?: CompanyStatusFilter): Promise<Blob> => {
    const params: Record<string, string> = {};
    if (licenceType) params.licenceType = licenceType;
    if (status) params.status = status;
    const response = await apiClient.get('/companies/export', { params, responseType: 'blob' });
    return response.data;
  },

  getById: async (id: number): Promise<Company> => {
    const response = await apiClient.get(`/companies/${id}`);
    return response.data.data;
  },

  create: async (companyData: CreateCompanyData): Promise<Company> => {
    preflight(createCompanyBody, companyData);
    const response = await apiClient.post('/companies', companyData);
    return response.data.data;
  },

  update: async (id: number, companyData: Partial<CreateCompanyData>): Promise<Company> => {
    preflight(updateCompanyBody, companyData);
    const response = await apiClient.put(`/companies/${id}`, companyData);
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/companies/${id}`);
  },

  // Company status management
  activate: async (id: number): Promise<Company> => {
    const response = await apiClient.patch(`/companies/${id}/activate`);
    return response.data.data;
  },

  deactivate: async (id: number): Promise<Company> => {
    const response = await apiClient.patch(`/companies/${id}/deactivate`);
    return response.data.data;
  },

  // API key management
  regenerateApiKey: async (id: number): Promise<{ apiKey: string }> => {
    const response = await apiClient.post(`/companies/${id}/regenerate-api-key`);
    return response.data.data;
  },

  // Licence type stats with YTD growth, optionally narrowed by status filter
  getLicenceStats: async (status?: CompanyStatusFilter): Promise<LicenceStatsResponse> => {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    const response = await apiClient.get('/companies/licence-stats', { params });
    return response.data.data;
  },

  // Company statistics
  getStats: async (id: number): Promise<{
    userCount: number;
    shipmentCount: number;
    totalEmissions: number;
    lastActivity: string;
  }> => {
    const response = await apiClient.get(`/companies/${id}/stats`);
    return response.data.data;
  },

  // Sub-customer operations
  getSubCustomers: async (companyId: number): Promise<SubCustomer[]> => {
    const response = await apiClient.get(`/companies/${companyId}/sub-customers`);
    return response.data.data;
  },

  createSubCustomer: async (subCustomerData: CreateSubCustomerData): Promise<any> => {
    preflight(createSubCustomerBody, subCustomerData);
    const response = await apiClient.post('/sub-customers', subCustomerData);
    // Backend returns { subCustomer, userAccountCreated, emailSent }
    return response.data.data;
  },

  updateSubCustomer: async (id: number, subCustomerData: Partial<CreateSubCustomerData>): Promise<SubCustomer> => {
    preflight(updateSubCustomerBody, subCustomerData);
    const response = await apiClient.put(`/sub-customers/${id}`, subCustomerData);
    return response.data.data;
  },

  deleteSubCustomer: async (id: number): Promise<void> => {
    await apiClient.delete(`/sub-customers/${id}`);
  },

  activateSubCustomer: async (id: number): Promise<SubCustomer> => {
    const response = await apiClient.patch(`/sub-customers/${id}/activate`);
    return response.data.data;
  },

  deactivateSubCustomer: async (id: number): Promise<SubCustomer> => {
    const response = await apiClient.patch(`/sub-customers/${id}/deactivate`);
    return response.data.data;
  },

  // Sub-company operations
  getSubCompanies: async (companyId: number): Promise<SubCompany[]> => {
    const response = await apiClient.get(`/sub-companies/company/${companyId}`);
    return response.data.data;
  },

  createSubCompany: async (subCompanyData: CreateSubCompanyData): Promise<SubCompany> => {
    preflight(createSubCompanyBody, subCompanyData);
    const response = await apiClient.post('/sub-companies', subCompanyData);
    return response.data.data;
  },

  updateSubCompany: async (id: number, subCompanyData: Partial<CreateSubCompanyData>): Promise<SubCompany> => {
    preflight(updateSubCompanyBody, subCompanyData);
    const response = await apiClient.put(`/sub-companies/${id}`, subCompanyData);
    return response.data.data;
  },

  deleteSubCompany: async (id: number): Promise<void> => {
    await apiClient.delete(`/sub-companies/${id}`);
  },

  activateSubCompany: async (id: number): Promise<SubCompany> => {
    const response = await apiClient.patch(`/sub-companies/${id}/activate`);
    return response.data.data;
  },

  deactivateSubCompany: async (id: number): Promise<SubCompany> => {
    const response = await apiClient.patch(`/sub-companies/${id}/deactivate`);
    return response.data.data;
  },

  // Branch operations
  getBranches: async (companyId: number): Promise<Branch[]> => {
    const response = await apiClient.get(`/branches/company/${companyId}`);
    return response.data.data;
  },

  getBranchesBySubCompany: async (subCompanyId: number): Promise<Branch[]> => {
    const response = await apiClient.get(`/branches/sub-company/${subCompanyId}`);
    return response.data.data;
  },

  createBranch: async (branchData: CreateBranchData): Promise<Branch> => {
    preflight(createBranchBody, branchData);
    const response = await apiClient.post('/branches', branchData);
    return response.data.data;
  },

  updateBranch: async (id: number, branchData: Partial<CreateBranchData>): Promise<Branch> => {
    preflight(updateBranchBody, branchData);
    const response = await apiClient.put(`/branches/${id}`, branchData);
    return response.data.data;
  },

  deleteBranch: async (id: number): Promise<void> => {
    await apiClient.delete(`/branches/${id}`);
  },

  activateBranch: async (id: number): Promise<Branch> => {
    const response = await apiClient.patch(`/branches/${id}/activate`);
    return response.data.data;
  },

  deactivateBranch: async (id: number): Promise<Branch> => {
    const response = await apiClient.patch(`/branches/${id}/deactivate`);
    return response.data.data;
  },
};